// Per-browser settings persisted to localStorage. Loaded once on app mount,
// written back whenever the settings modal mutates them.
//
// Versioned key so the shape can evolve — bump the version and add a
// migration here if/when the schema changes.

const STORAGE_KEY = 'wiznaibus.prefs.v1';

export const DEFAULT_PREFS = Object.freeze({
  // When true, the three baked-in highlighted users (Luxor + friends) are
  // not shown in the entrants/snubs callouts — and also don't fire the
  // start-of-tournament announcement modal.
  hideDefaultHighlights: false,
  // When true, the bracket section is hidden — the team grid renders
  // immediately at the top of the page.
  hideBracket: false,
  // User-added names to highlight in the entrants/snubs callouts. Each
  // entry is { name: string, color: string } — color is a CSS color
  // string (the `<input type="color">` produces hex like "#ff0000").
  customHighlights: [],
  // When true, the big start-of-tournament announcement modal is
  // suppressed even when there would be matches to announce.
  disableAnnouncements: false,
  // Notification sounds (lib/sounds.js). `soundSet` picks the synth palette;
  // `soundEvents` is a per-event on/off map. All default OFF (opt-in). Migrated
  // from the old `newTournamentSound` boolean on load (see loadPrefs).
  soundSet: 'chime',
  soundEvents: { newTournament: false, matchUp: false, win: false, loss: false, champ: false },
  // Notification volume multiplier: 1 = 100% (each set's tuned level), 0 = muted,
  // up to 2 = 200% (louder). Applied in lib/sounds.js.
  soundVolume: 1,
  // Expanded cards in the old team grid. Nothing reads it since the 2026-10 team section
  // replaced that grid (and Settings no longer shows it); kept so saved prefs still load.
  expandedCards: false,
  // Color gear and skills by win rate (lib/winRateColor). On by default since 2026-10-24 (chat missed the colors);
  // winRatesOn marks that the one-time switch-on below has run, so turning it off afterwards sticks.
  showWinRates: true,
  winRatesOn: true,
  // When true (default), the live-match bracket sprites occasionally play their
  // armed attack animation. Always suppressed under prefers-reduced-motion.
  attackAnims: true,
  // When true (default), the bracket page shows the 🎉 "just happened" strip of
  // recent titles, quest completions and long reigns (RecentTicker.vue).
  showTicker: true,
  // Twitch username for the "My Profile" quick-access button (👤 in the top
  // bar). When set, also auto-highlights on the bracket in gold.
  myUsername: '',
  myColor: '#ffd700',
  // Tournament IDs whose start-of-tournament announcement has already
  // been dismissed in this browser. Capped at the most recent 50 to
  // keep localStorage bounded.
  seenAnnouncements: [],
  // Feature spotlight IDs the user has already seen (one-time "what's new" popups).
  seenUpdates: [],
  // Active quest/achievement goal for the player profile tracker.
  // Shape: { type: 'quest'|'achievement', id: string } or null.
  activeGoal: null,
  // Saved goal progress for the bracket widget: written by the profile page, and rebuilt
  // by the bracket page whenever newer data is out (lib/goalRefresh.js); src = the data
  // build it came from. Shape: { name, remaining: string[], status, src? } or null.
  goalInfo: null,
  // When true, the bracket page goal widget is hidden. Cleared when the
  // user sets a new goal on the profile page.
  goalDismissed: false,
});

const SEEN_CAP = 50;

// The baked-in highlighted users (Luxor's crew) + the color they're shown in by
// default, so they stand out in the callouts/announcement/bracket even before the
// user customizes anything.
export const DEFAULT_HIGHLIGHTED = ['luxordoesntframe', 'yoimbear', 'firstodie'];
export const DEFAULT_HIGHLIGHT_COLOR = '#ffd700';

// Resolve the effective highlighted-user list from prefs: the defaults (unless
// disabled) plus the user's custom entries, DEDUPED by lowercased name — a custom
// entry overrides the matching default so the user's chosen color wins, and the
// same user never appears twice. Returns [{ name (lowercased), color }]; an empty
// color means "use the default site styling".
export function resolveHighlightedUsers(prefs) {
  const byName = new Map();
  const norm = s => s.trim().toLowerCase().replace(/_/g, ' ');
  // "Who are you" username — auto-highlighted in gold (lowest priority, so a
  // custom entry or a matching default overrides its color).
  if (prefs && prefs.myUsername) {
    const me = norm(prefs.myUsername);
    if (me) byName.set(me, { name: me, color: prefs.myColor || DEFAULT_HIGHLIGHT_COLOR });
  }
  if (prefs && !prefs.hideDefaultHighlights) {
    for (const n of DEFAULT_HIGHLIGHTED) byName.set(n, { name: n, color: DEFAULT_HIGHLIGHT_COLOR });
  }
  for (const u of (prefs && prefs.customHighlights) || []) {
    const n = norm(u.name || '');
    if (n) byName.set(n, { name: n, color: u.color || '' });
  }
  return [...byName.values()];
}

export function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    // Migrate the old single newTournamentSound boolean → soundEvents map.
    const soundEvents = {
      ...DEFAULT_PREFS.soundEvents,
      ...(parsed.soundEvents || {}),
      ...(parsed.newTournamentSound != null && parsed.soundEvents == null
        ? { newTournament: !!parsed.newTournamentSound }
        : {}),
    };
    // Coerce/clamp the volume multiplier so a bad stored value can't override the
    // sane default (the spread above would otherwise let e.g. 5 or "loud" through).
    const sv = Number(parsed.soundVolume);
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      soundSet: parsed.soundSet || DEFAULT_PREFS.soundSet,
      soundEvents,
      soundVolume: Number.isFinite(sv) ? Math.max(0, Math.min(2, sv)) : 1,
      // saved before win-rate colors were on by default: everyone's stored false was the old default, not a choice
      ...(parsed.winRatesOn ? {} : { showWinRates: true, winRatesOn: true }),
      customHighlights: Array.isArray(parsed.customHighlights)
        ? parsed.customHighlights.map(u => ({ name: u.name || '', color: u.color || '#ffd700' }))
        : [],
      seenAnnouncements: Array.isArray(parsed.seenAnnouncements)
        ? parsed.seenAnnouncements.slice(-SEEN_CAP)
        : [],
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePrefs(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage can be unavailable (private browsing, quota, etc.) —
    // we silently no-op rather than crash the page.
  }
}

// Append a tournament ID to seenAnnouncements and prune to the cap.
// Returns a new array — caller is responsible for putting it back on prefs.
export function markAnnouncementSeen(seen, tournamentId) {
  if (!tournamentId) return seen || [];
  if ((seen || []).includes(tournamentId)) return seen;
  const next = [...(seen || []), tournamentId];
  return next.length > SEEN_CAP ? next.slice(-SEEN_CAP) : next;
}
